// useTheme: loads the user's selected theme + unlocked themes from
// `user_themes`, applies it to :root, auto-unlocks themes as the user
// earns the qualifying badges/levels, and exposes a setter.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { applyTheme, DEFAULT_THEME, isThemeUnlocked, THEMES, type ThemeId, getTheme } from '@/lib/themes';
import { usePebble } from './usePebble';
import { useAchievements } from './useAchievements';
import { useSubscription } from './useSubscription';
import { tierFor } from '@/lib/types';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';

interface ThemeRow {
  selected_theme: string;
  unlocked_themes: string[];
}

export function useTheme() {
  const store = usePebble();
  const userId = store.getUserId();
  const xp = store.totalXp();
  const { level } = tierFor(xp);
  const { earnedSet } = useAchievements();
  const { isPro } = useSubscription();

  const [selected, setSelected] = useState<ThemeId>(DEFAULT_THEME);
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set([DEFAULT_THEME]));
  const [loaded, setLoaded] = useState(false);

  // Load row
  useEffect(() => {
    if (!userId) {
      applyTheme(DEFAULT_THEME);
      setSelected(DEFAULT_THEME);
      setUnlocked(new Set([DEFAULT_THEME]));
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_themes')
        .select('selected_theme, unlocked_themes')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      const row = data as ThemeRow | null;
      const sel = (row?.selected_theme ?? DEFAULT_THEME) as ThemeId;
      const unl = new Set<string>(row?.unlocked_themes ?? [DEFAULT_THEME]);
      setSelected(sel);
      setUnlocked(unl);
      applyTheme(sel);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Auto-unlock newly-eligible themes
  useEffect(() => {
    if (!userId || !loaded) return;
    const ctx = { earnedBadges: earnedSet, level, isPro };
    const newlyUnlocked = THEMES.filter(
      t => !unlocked.has(t.id) && isThemeUnlocked(t, ctx)
    );
    if (newlyUnlocked.length === 0) return;
    const nextUnlocked = new Set(unlocked);
    for (const t of newlyUnlocked) nextUnlocked.add(t.id);
    setUnlocked(nextUnlocked);
    (async () => {
      await supabase.from('user_themes').upsert({
        user_id: userId,
        selected_theme: selected,
        unlocked_themes: Array.from(nextUnlocked),
      }, { onConflict: 'user_id' });
      for (const t of newlyUnlocked) {
        toast.success(`🎨 New theme unlocked: ${t.name}`);
        track('beta_feedback_clicked', { kind: 'theme_unlocked', theme_id: t.id });
      }
    })();
  }, [earnedSet, level, isPro, loaded, userId, unlocked, selected]);

  const select = useCallback(async (themeId: ThemeId) => {
    if (!unlocked.has(themeId)) return;
    setSelected(themeId);
    applyTheme(themeId);
    track('beta_feedback_clicked', { kind: 'theme_selected', theme_id: themeId });
    if (!userId) return;
    await supabase.from('user_themes').upsert({
      user_id: userId,
      selected_theme: themeId,
      unlocked_themes: Array.from(unlocked),
    }, { onConflict: 'user_id' });
  }, [unlocked, userId]);

  return {
    selected,
    selectedTheme: getTheme(selected),
    unlocked,
    select,
    loaded,
  };
}
